import { useRef } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { MoveLeft, MoveRight, Trash2, Upload as UploadIcon } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import { imagesApi } from '@/api/endpoints/images'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'

export function ProductImagesTab({ sku }: { sku: string }) {
  const queryClient = useQueryClient()
  const { t: tProducts } = useTranslation('products')
  const { data, isLoading, error } = useQuery({
    queryKey: ['product-images', sku],
    queryFn: () => imagesApi.list(sku),
  })
  const items = data?.items ?? []
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const remaining = Math.max(0, 6 - items.length)

  const openPicker = () => fileInputRef.current?.click()

  const attachMutation = useMutation({
    mutationFn: async (files: Array<File>) => {
      for (const file of files) {
        const up = await imagesApi.getUploadUrl(sku, {
          filename: file.name,
          contentType: file.type,
        })
        await imagesApi.directUpload(up.uploadURL, file)
        await imagesApi.attach(sku, { imageId: up.imageId })
      }
    },
    onSuccess: () => {
      toast.success(tProducts('images.uploadSuccess'))
      queryClient.invalidateQueries({ queryKey: ['product-images', sku] })
    },
    onError: () => toast.error(tProducts('images.uploadFailed')),
  })

  const onFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : []
    if (files.length === 0) return
    const allowed = files.slice(0, remaining)
    if (files.length > allowed.length) {
      toast.message(tProducts('images.uploadIgnored'))
    }
    attachMutation.mutate(allowed)
    e.target.value = ''
  }

  const updateAlt = async (imageId: string, alt: string) => {
    await imagesApi.update(sku, imageId, { alt })
    queryClient.invalidateQueries({ queryKey: ['product-images', sku] })
  }

  const reorder = async (index: number, dir: -1 | 1) => {
    const arr = [...items]
    const j = index + dir
    if (j < 0 || j >= arr.length) return
    ;[arr[index], arr[j]] = [arr[j], arr[index]]
    // Normalize sort order
    const reordered = arr.map((it, idx) => ({ imageId: it.id, sortOrder: idx }))
    await imagesApi.reorder(sku, reordered)
    queryClient.invalidateQueries({ queryKey: ['product-images', sku] })
  }

  const remove = async (imageId: string) => {
    await imagesApi.remove(sku, imageId)
    queryClient.invalidateQueries({ queryKey: ['product-images', sku] })
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-1/3" />
        <Skeleton className="h-32 w-full" />
      </div>
    )
  }
  if (error)
    return (
      <p className="text-sm text-destructive">
        {tProducts('images.loadError')}
      </p>
    )

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground">
            {tProducts('images.helpPrimary')}
          </p>
          <p className="text-xs text-muted-foreground">
            {tProducts('images.helpSecondary')}
          </p>
        </div>
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={onFilesSelected}
          />
          <Button
            onClick={openPicker}
            disabled={remaining === 0 || attachMutation.isPending}
          >
            <UploadIcon className="w-4 h-4 mr-2" />
            {remaining > 0
              ? tProducts('images.addImages', { remaining })
              : tProducts('images.limitReached')}
          </Button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="border rounded-md p-8 text-center text-muted-foreground">
          {tProducts('images.empty')}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((img, idx) => (
            <div
              key={img.id}
              className="border rounded-md p-3 flex gap-3 items-start"
            >
              <img
                src={img.thumbUrl}
                alt={img.alt || ''}
                className="w-24 h-24 object-cover rounded"
              />
              <div className="flex-1 space-y-2">
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => reorder(idx, -1)}
                    disabled={idx === 0}
                  >
                    <MoveLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => reorder(idx, 1)}
                    disabled={idx === items.length - 1}
                  >
                    <MoveRight className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="destructive"
                    size="icon"
                    onClick={() => remove(img.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                <div>
                  <Label className="text-xs">
                    {tProducts('images.altLabel')}
                  </Label>
                  <Input
                    defaultValue={img.alt || ''}
                    placeholder={tProducts('images.altPlaceholder')}
                    onBlur={(e) => updateAlt(img.id, e.target.value)}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
